import get from 'lodash/get';

import { resolveArchitectureOptions } from '../../../capabilities/compute';
import { buildImageVmQuery, isExistingSystemDisk, normalizeSystemDiskRecord } from '../../../capabilities/image-vm/utils';
import type { LinkageRule } from '../../../platform/linkage/types';
import { readGpu, readSystemDiskImageArchitecture } from '../../_shared/common/formHelpers';
import { applyImagePresetServicesEffect } from '../../_shared/image-network-linkage/linkage/applyImagePresetServices';
import {
  createNetworkContextLinkageRules,
  createStorageContextLinkageRules,
} from '../../_shared/image-network-linkage/linkage/contextRules';

const SERVICES_PATH = 'resource.network.service';

export interface VmImageVmLinkageProps {
  imageQuery?: Record<string, unknown>;
  namespace?: string;
}

export interface VmComputeLinkageProps {
  architectureOptions?: string[];
  namespace?: string;
  vmName?: string;
}

export interface VmNetworkLinkageProps {
  workspace?: string;
  datacenter?: string;
}

export interface VmStorageLinkageProps {
  namespace?: string;
  containerName?: string;
  datacenter?: string;
}

export type VmLinkagePropsMap = Record<string, unknown> & {
  'image-vm'?: VmImageVmLinkageProps;
  compute?: VmComputeLinkageProps;
  network?: VmNetworkLinkageProps;
  storage?: VmStorageLinkageProps;
};

function applyServicesFromDiskEffect(ctx: { form: import('@arco-design/web-react').FormInstance; values: Record<string, unknown> }) {
  const disk = normalizeSystemDiskRecord(ctx.values.systemDisk);
  if (isExistingSystemDisk(disk)) {
    return;
  }
  applyImagePresetServicesEffect(ctx, SERVICES_PATH);
}

export const VM_LINKAGE_RULES: LinkageRule<VmLinkagePropsMap>[] = [
  {
    id: 'image.applyServices',
    watch: 'systemDisk',
    target: 'image-vm',
    effect: applyServicesFromDiskEffect,
  },
  {
    id: 'arch.imageQuery',
    watch: ['systemDisk', 'resource.gpu'],
    target: 'image-vm',
    derive: ({ values }) => ({
      imageQuery: buildImageVmQuery(readSystemDiskImageArchitecture(values), readGpu(values), 'cloudHost'),
    }),
  },
  {
    id: 'arch.computeOptions',
    watch: 'systemDisk',
    target: 'compute',
    derive: ({ values }) => {
      const disk = normalizeSystemDiskRecord(values.systemDisk);
      if (isExistingSystemDisk(disk)) {
        return { architectureOptions: resolveArchitectureOptions(undefined) };
      }
      return {
        architectureOptions: resolveArchitectureOptions(values.systemDisk),
      };
    },
  },
  {
    id: 'baseInfo.computeContext',
    watch: ['namespace', 'name'],
    target: 'compute',
    derive: ({ values }) => ({
      namespace: values.namespace as string | undefined,
      vmName: values.name as string | undefined,
    }),
  },
  {
    id: 'baseInfo.imageVmContext',
    watch: ['namespace', 'name'],
    target: 'image-vm',
    derive: ({ values }) => ({
      namespace: values.namespace as string | undefined,
    }),
  },
  ...createNetworkContextLinkageRules<VmLinkagePropsMap>(),
  ...createStorageContextLinkageRules<VmLinkagePropsMap>(['storage']),
];
